// Every assistant module in one place: what it's for, where it runs, and how to build its tools for the schema check.
// A new module must be added here, or the strict-schema test fails

import type { Tool } from "ai"
import type { ModuleCatalogEntry } from "../types"
import { makeAppModule } from "./app"
import { makeResearchModule } from "./research"
import { formsCatalog, makeFormsModule } from "./forms"
import { channelsCatalog, makeChannelsModule } from "./channels"
import { makeTeamsModule } from "./teams"
import { smartCmsCatalog, SMART_CMS_TOOL_FACTORIES } from "./smartCms"
import { flowEditorCatalog, flowEditorToolsForCheck } from "./flowEditor"
import { dashboardCatalog, DASHBOARD_TOOLS_SOURCE } from "./dashboard"

export interface ModuleEntry {
  id: string
  // "always": registered by the assistant itself. "page": registered by its page while that page is open
  runs: "always" | "page"
  // How the assistant finds it when it isn't open. Modules that are always open without a page of their own have none
  catalog?: ModuleCatalogEntry
  // Every tool it can offer, built with stand-in dependencies
  toolsForCheck?: (stub: any) => Record<string, Tool>
  // For tools built inside a React hook: the file the schema check reads instead
  toolsSource?: string
}

const cms = (id: string): ModuleEntry => ({
  id, runs: "page",
  catalog: smartCmsCatalog.find(c => c.id === id),
  toolsForCheck: SMART_CMS_TOOL_FACTORIES[id],
})

export const MODULES: ModuleEntry[] = [
  { id: "app", runs: "always", toolsForCheck: stub => makeAppModule(stub).tools },
  { id: "research", runs: "always", toolsForCheck: stub => makeResearchModule({ modelId: "openai/gpt-5.6-luna", apiKeys: {}, getCorpusIds: () => [], attachedCorpora: [] }).tools },
  { id: "forms", runs: "always", catalog: formsCatalog, toolsForCheck: stub => makeFormsModule({ teamId: "t", onFormsPage: false, ask: async () => true }).tools },
  { id: "channels", runs: "always", catalog: channelsCatalog, toolsForCheck: () => makeChannelsModule({ teamId: "t", ask: async () => true }).tools },
  { id: "teams", runs: "always", toolsForCheck: () => makeTeamsModule({ currentTeamId: () => "t", teams: () => [], switchTeam: async () => {}, ask: async () => true }).tools },
  cms("cms.articles"),
  cms("cms.serviceMap"),
  cms("cms.siteConfig"),
  { id: "flow.editor", runs: "page", catalog: flowEditorCatalog, toolsForCheck: flowEditorToolsForCheck },
  { id: "dashboard", runs: "page", catalog: dashboardCatalog, toolsSource: DASHBOARD_TOOLS_SOURCE },
]

// What the assistant is told it can open, and what team skills can be scoped to
export const MODULE_CATALOG: ModuleCatalogEntry[] = MODULES.flatMap(m => (m.catalog ? [m.catalog] : []))
